import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Popup, useMap, Tooltip, Marker } from "react-leaflet";
import L from "leaflet";
import { toast, Toaster } from "sonner";
import {
  Upload, Map as MapIcon, Search, Eye, EyeOff, Target, Download,
  Users, Building2, CheckCircle2, AlertTriangle, RotateCcw, FileSpreadsheet,
  Sliders, Loader2, Layers, X, Table as TableIcon, FileText, Grid3x3
} from "lucide-react";
import { IDS } from "../constants/testIds";
import DataDrawer from "../components/DataDrawer";
import ColorPicker from "../components/ColorPicker";
import { exportDealerReport } from "../lib/pdfExport";
import * as XLSX from "xlsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TURKEY_CENTER = [39.0, 35.5];
const TURKEY_BOUNDS = [[35.5, 25.0], [42.5, 45.5]];

// Helper component for programmatic map control + zoom tracking
function MapController({ target, resetKey, onZoomChange }) {
  const map = useMap();
  useEffect(() => {
    if (target && target.bounds) {
      map.flyToBounds(target.bounds, { padding: [40, 40], duration: 0.8 });
    } else if (target && target.center) {
      map.flyTo(target.center, target.zoom || 8, { duration: 0.8 });
    }
  }, [target, map]);

  useEffect(() => {
    if (resetKey) map.flyToBounds(TURKEY_BOUNDS, { padding: [20, 20], duration: 0.6 });
  }, [resetKey, map]);

  useEffect(() => {
    if (!onZoomChange) return;
    const handler = () => onZoomChange(map.getZoom());
    map.on("zoomend", handler);
    onZoomChange(map.getZoom());
    return () => map.off("zoomend", handler);
  }, [map, onZoomChange]);
  return null;
}

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    data-testid={testId}
    className="bg-[#111726] border border-white/10 rounded-lg p-3 flex flex-col gap-1"
  >
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
        <Icon size={14} strokeWidth={2.2} />
      </div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
    </div>
    <div className="font-mono text-xl font-bold text-slate-100 tabular-nums pl-8">{value}</div>
  </div>
);

const DealerRow = ({ dealer, visible, onToggle, onIsolate, onFocus, isolated, onColorChange, onColorReset }) => (
  <div
    data-testid={IDS.dealerItem(dealer.id)}
    className={`group flex items-center gap-2 px-2 py-2 rounded-md hover:bg-white/5 transition-colors cursor-pointer border-l-2 ${visible ? "" : "opacity-40"}`}
    style={{ borderLeftColor: visible ? dealer.stroke : "transparent" }}
    onClick={() => onFocus(dealer)}
  >
    <button
      data-testid={IDS.dealerToggle(dealer.id)}
      onClick={(e) => { e.stopPropagation(); onToggle(dealer.id); }}
      className="flex-shrink-0 w-4 h-4 rounded-sm border border-white/20 flex items-center justify-center"
      style={{ background: visible ? dealer.fill : "transparent" }}
      title={visible ? "Gizle" : "Göster"}
    >
      {visible && <CheckCircle2 size={10} className="text-white" />}
    </button>
    <ColorPicker
      currentFill={dealer.fill}
      onSelect={(p) => onColorChange(dealer.id, p)}
      onReset={() => onColorReset(dealer.id)}
    />
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-medium text-slate-100 truncate">{dealer.name}</div>
      <div className="text-[10px] text-slate-500 font-mono">
        {dealer.inliers} nokta {dealer.outliers > 0 && <span className="text-amber-400">· {dealer.outliers} outlier</span>}
      </div>
    </div>
    <button
      data-testid={IDS.dealerIsolate(dealer.id)}
      onClick={(e) => { e.stopPropagation(); onIsolate(dealer.id); }}
      className={`flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity px-1.5 py-0.5 text-[10px] rounded font-semibold ${isolated ? "bg-blue-500 text-white opacity-100" : "bg-white/10 text-slate-300 hover:bg-white/20"}`}
      title="Yalnızca bunu göster"
    >
      <Target size={11} />
    </button>
  </div>
);

export default function DealerMap() {
  const [data, setData] = useState(null); // {geojson, outliers, dealers, stats}
  const [loading, setLoading] = useState(false);
  const [uploadedName, setUploadedName] = useState(null);
  const [outlierPct, setOutlierPct] = useState(98);
  const [search, setSearch] = useState("");
  const [visibleSet, setVisibleSet] = useState(new Set());
  const [showPoints, setShowPoints] = useState(false);
  const [showOutliers, setShowOutliers] = useState(true);
  const [flyTarget, setFlyTarget] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [tileStyle, setTileStyle] = useState("mahalle"); // standart | mahalle | dark
  const [colorOverrides, setColorOverrides] = useState({}); // dealerId -> {fill, stroke}
  const [viewMode, setViewMode] = useState("voronoi"); // voronoi | admin
  const [adminGeo, setAdminGeo] = useState({ il: null, ilce: null });
  const [mapZoom, setMapZoom] = useState(6);
  const [dragOver, setDragOver] = useState(false);
  const [isolatedId, setIsolatedId] = useState(null);
  const [hoveredDealer, setHoveredDealer] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const fileInputRef = useRef();
  const mapWrapperRef = useRef();

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error("Lütfen .xlsx, .xls veya .csv dosyası yükleyin");
      return;
    }
    setLoading(true);
    setUploadedName(file.name);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/upload?outlier_pct=${outlierPct}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setData(res.data);
      setVisibleSet(new Set(res.data.dealers.map(d => d.id)));
      setIsolatedId(null);
      toast.success(`${res.data.stats.totalDealers} bayi, ${res.data.stats.validCustomers} müşteri yüklendi`);
      // fit to Turkey bounds after load
      setResetKey(k => k + 1);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error("Yükleme başarısız: " + msg);
      setUploadedName(null);
    } finally {
      setLoading(false);
    }
  }, [outlierPct]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  // Lazy-load admin (il/ilçe) geometry when needed
  useEffect(() => {
    if (viewMode !== "admin") return;
    let cancelled = false;
    (async () => {
      try {
        const need = [];
        if (!adminGeo.il) need.push("il");
        if (!adminGeo.ilce) need.push("ilce");
        if (!need.length) return;
        const results = await Promise.all(
          need.map(k => axios.get(`${API}/geo/${k}`).then(r => [k, r.data]))
        );
        if (cancelled) return;
        setAdminGeo(prev => {
          const next = { ...prev };
          for (const [k, g] of results) next[k] = g;
          return next;
        });
      } catch (e) {
        toast.error("İl/ilçe verisi alınamadı: " + (e?.message || ""));
      }
    })();
    return () => { cancelled = true; };
  }, [viewMode, adminGeo.il, adminGeo.ilce]);

  // Apply color overrides on-the-fly to dealers, geojson, and outlier list
  const dealersView = useMemo(() => {
    if (!data) return [];
    return data.dealers.map(d => {
      const ov = colorOverrides[d.id];
      return ov ? { ...d, fill: ov.fill, stroke: ov.stroke, colorName: ov.name } : d;
    });
  }, [data, colorOverrides]);

  const filteredDealers = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLocaleLowerCase("tr");
    return dealersView.filter(d => !q || d.name.toLocaleLowerCase("tr").includes(q));
  }, [dealersView, data, search]);

  const displayedIds = useMemo(() => {
    if (isolatedId) return new Set([isolatedId]);
    return visibleSet;
  }, [visibleSet, isolatedId]);

  const filteredGeojson = useMemo(() => {
    if (!data) return null;
    const dealerById = Object.fromEntries(dealersView.map(d => [d.id, d]));
    return {
      type: "FeatureCollection",
      features: data.geojson.features
        .filter(f => displayedIds.has(f.properties.dealerId))
        .map(f => {
          const d = dealerById[f.properties.dealerId];
          if (!d) return f;
          return { ...f, properties: { ...f.properties, fill: d.fill, stroke: d.stroke } };
        })
    };
  }, [data, displayedIds, dealersView]);

  // Admin layer selection based on zoom (<8: il, >=8: ilçe)
  const adminLayerKind = mapZoom >= 8 ? "ilce" : "il";

  const adminGeojson = useMemo(() => {
    if (!data || viewMode !== "admin") return null;
    const src = adminGeo[adminLayerKind];
    if (!src) return null;
    const assignments = adminLayerKind === "il" ? data.ilAssignments : data.ilceAssignments;
    if (!assignments) return null;
    const dealerById = Object.fromEntries(dealersView.map(d => [d.id, d]));
    const feats = [];
    for (const f of src.features) {
      const key = f.properties.key;
      const a = assignments[key];
      if (!a) continue;
      const dealer = dealerById[a.dealerId];
      if (!dealer) continue;
      if (!displayedIds.has(a.dealerId)) continue;
      feats.push({
        type: "Feature",
        geometry: f.geometry,
        properties: {
          name: f.properties.name,
          dealer: a.dealer,
          dealerId: a.dealerId,
          count: a.count,
          source: a.source,
          fill: dealer.fill,
          stroke: dealer.stroke,
        }
      });
    }
    return { type: "FeatureCollection", features: feats };
  }, [data, viewMode, adminGeo, adminLayerKind, dealersView, displayedIds]);

  const filteredOutliers = useMemo(() => {
    if (!data || !showOutliers) return null;
    const dealerNameToId = Object.fromEntries(data.dealers.map(d => [d.name, d.id]));
    return {
      type: "FeatureCollection",
      features: data.outliers.features.filter(f => displayedIds.has(dealerNameToId[f.properties.dealer]))
    };
  }, [data, showOutliers, displayedIds]);

  const geoJsonKey = useMemo(
    () => Array.from(displayedIds).join(",") + ":" + data?.stats?.outlierPct + ":" + showLabels + ":" + Object.keys(colorOverrides).length + Object.values(colorOverrides).map(o=>o.fill).join("") + ":" + viewMode + ":" + adminLayerKind,
    [displayedIds, data, showLabels, colorOverrides, viewMode, adminLayerKind]
  );

  const onFocus = (dealer) => {
    if (dealer.points && dealer.points.length) {
      const lats = dealer.points.map(p => p.lat);
      const lngs = dealer.points.map(p => p.lng);
      const bounds = [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
      setFlyTarget({ bounds, ts: Date.now() });
    } else if (dealer.centroid) {
      setFlyTarget({ center: [dealer.centroid[1], dealer.centroid[0]], zoom: 10, ts: Date.now() });
    }
  };

  const onToggle = (id) => {
    setVisibleSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onIsolate = (id) => {
    setIsolatedId(prev => prev === id ? null : id);
  };

  const toggleAll = () => {
    if (!data) return;
    if (visibleSet.size === data.dealers.length) setVisibleSet(new Set());
    else setVisibleSet(new Set(data.dealers.map(d => d.id)));
  };

  const onColorChange = (dealerId, palette) => {
    setColorOverrides(prev => ({ ...prev, [dealerId]: { fill: palette.fill, stroke: palette.stroke, name: palette.name } }));
  };
  const onColorReset = (dealerId) => {
    setColorOverrides(prev => {
      const next = { ...prev };
      delete next[dealerId];
      return next;
    });
  };

  const exportGeoJSON = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data.geojson, null, 2)], { type: "application/geo+json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bayi-bolgeleri-${Date.now()}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("GeoJSON indirildi");
  };

  const exportOutliersCsv = () => {
    if (!data || !data.outliers) return;
    // Filter to currently displayed dealers only (respects toggle + isolate)
    const dealerNameToId = Object.fromEntries((data.dealers || []).map(d => [d.name, d.id]));
    const rows = data.outliers.features.filter(f => displayedIds.has(dealerNameToId[f.properties.dealer]));
    if (!rows.length) { toast.info("Seçili bayiler için outlier bulunamadı"); return; }

    const wsData = [
      ["SAP Kodu", "Bayi Adı", "Enlem (x)", "Boylam (y)", "Merkez Uzaklığı (derece)"],
      ...rows.map(f => {
        const [lng, lat] = f.geometry.coordinates;
        return [f.properties.sap, f.properties.dealer, Number(lat.toFixed(6)), Number(lng.toFixed(6)), Number((f.properties.dist ?? 0).toFixed(6))];
      })
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 14 }, { wch: 32 }, { wch: 12 }, { wch: 12 }, { wch: 20 }];
    ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: 4 } }) };

    // Freeze header
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Outlier Noktalar");

    // Summary sheet: outliers per dealer
    const counts = {};
    rows.forEach(f => { counts[f.properties.dealer] = (counts[f.properties.dealer] || 0) + 1; });
    const summary = [["Bayi Adı", "Outlier Sayısı"], ...Object.entries(counts).sort((a,b) => b[1]-a[1])];
    const wsSum = XLSX.utils.aoa_to_sheet(summary);
    wsSum["!cols"] = [{ wch: 32 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, wsSum, "Bayi Özet");

    const fileName = `outlier-noktalar-${new Date().toISOString().slice(0,10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    const totalOutliers = data.outliers.features.length;
    const suffix = rows.length === totalOutliers ? "" : ` (${rows.length}/${totalOutliers} seçili bayiler)`;
    toast.success(`${rows.length} outlier Excel olarak indirildi${suffix}`);
  };

  const exportPdf = async () => {
    if (!data) return;
    setExportingPdf(true);
    try {
      const selectedDealers = data.dealers.filter(d => displayedIds.has(d.id));
      await exportDealerReport({
        mapElement: mapWrapperRef.current,
        dealers: selectedDealers,
        stats: data.stats,
        uploadedName,
      });
      toast.success(`PDF indirildi (${selectedDealers.length} bayi)`);
    } catch (e) {
      toast.error("PDF oluşturulamadı: " + (e?.message || ""));
    } finally {
      setExportingPdf(false);
    }
  };

  const clearData = () => {
    setData(null);
    setUploadedName(null);
    setVisibleSet(new Set());
    setIsolatedId(null);
  };

  const geoJsonStyle = (feature) => ({
    fillColor: feature.properties.fill,
    color: feature.properties.stroke,
    weight: hoveredDealer === feature.properties.dealerId ? 2.5 : 1,
    fillOpacity: hoveredDealer === feature.properties.dealerId ? 0.75 : 0.55,
    opacity: 1,
    lineJoin: "round",
    lineCap: "round",
  });

  const onEachFeature = (feature, layer) => {
    const p = feature.properties;
    layer.bindTooltip(
      `<div style="font-family: 'Plus Jakarta Sans'; padding: 2px 4px;">
        <div style="font-weight: 700; color: #F8FAFC; margin-bottom: 4px;">${p.dealer}</div>
        <div style="font-size: 11px; color: #94A3B8; font-family: 'JetBrains Mono';">${p.count} müşteri · ${p.outliers} outlier</div>
      </div>`,
      { sticky: true, direction: "top", offset: [0, -8], opacity: 0.95, className: "" }
    );
    if (showLabels) {
      // permanent label handled via separate Marker layer (works with canvas polygons)
    }
    layer.on("mouseover", () => setHoveredDealer(p.dealerId));
    layer.on("mouseout", () => setHoveredDealer(null));
    layer.on("click", () => {
      const dealer = data?.dealers?.find(d => d.id === p.dealerId);
      if (dealer) onFocus(dealer);
    });
  };

  const tileConfig = tileStyle === "dark"
    ? {
        url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        attribution: '&copy; OpenStreetMap · &copy; CARTO',
      }
    : tileStyle === "mahalle"
    ? {
        url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        attribution: '&copy; OpenStreetMap · &copy; CARTO (mahalle detayı)',
      }
    : {
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution: '&copy; OpenStreetMap contributors',
      };

  return (
    <div data-testid={IDS.appContainer} className="App h-screen w-screen overflow-hidden flex flex-col bg-[#0B0F17] text-slate-100">
      <Toaster theme="dark" position="top-right" richColors />

      {/* Header */}
      <header className="h-14 flex-shrink-0 bg-[#111726]/90 backdrop-blur-md border-b border-white/10 px-4 flex items-center justify-between z-30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <MapIcon size={18} className="text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-extrabold text-[15px] tracking-tight leading-none">TürkBayi Harita</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mt-0.5">Bayilik Bölge Analizi</div>
          </div>
          {uploadedName && (
            <div className="ml-4 flex items-center gap-1.5 text-[11px] text-slate-400 bg-white/5 px-2 py-1 rounded font-mono">
              <FileSpreadsheet size={12} />
              {uploadedName}
              <button data-testid={IDS.clearData} onClick={clearData} className="ml-1 hover:text-red-400"><X size={11} /></button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="export-outliers-xlsx-button"
            disabled={!data || !data.outliers?.features?.length}
            onClick={exportOutliersCsv}
            className="text-[12px] px-3 py-1.5 rounded-md bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5 font-semibold"
            title="Seçili bayilerin outlier noktalarını Excel (.xlsx) olarak indir"
          >
            <AlertTriangle size={13} /> Outlier XLSX
          </button>
          <button
            data-testid="data-drawer-toggle"
            disabled={!data}
            onClick={() => setDrawerOpen(o => !o)}
            className="text-[12px] px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5 font-semibold border border-white/10"
          >
            <TableIcon size={13} /> Veri
          </button>
          <button
            data-testid="export-pdf-button"
            disabled={!data || exportingPdf}
            onClick={exportPdf}
            className="text-[12px] px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5 font-semibold border border-white/10"
          >
            {exportingPdf ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} PDF
          </button>
          <button
            data-testid={IDS.exportGeojson}
            disabled={!data}
            onClick={exportGeoJSON}
            className="text-[12px] px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5 font-semibold border border-white/10"
          >
            <Download size={13} /> GeoJSON
          </button>
          <button
            data-testid={IDS.uploadTrigger}
            onClick={() => fileInputRef.current?.click()}
            className="text-[12px] px-3 py-1.5 rounded-md bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-1.5 font-semibold"
          >
            <Upload size={13} /> Excel Yükle
          </button>
          <input
            ref={fileInputRef}
            data-testid={IDS.fileInput}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
        </div>
      </header>

      {/* Main */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-80 lg:w-96 flex-shrink-0 flex flex-col border-r border-white/10 bg-[#0B0F17] overflow-hidden">
          {/* Stats */}
          <div className="p-3 grid grid-cols-2 gap-2 border-b border-white/10">
            <StatCard testId={IDS.statTotalCustomers} icon={Users} label="Müşteri" value={data?.stats?.validCustomers ?? "—"} accent="#3B82F6" />
            <StatCard testId={IDS.statTotalDealers} icon={Building2} label="Bayi" value={data?.stats?.totalDealers ?? "—"} accent="#10B981" />
            <StatCard testId={IDS.statInliers} icon={CheckCircle2} label="İç Nokta" value={data?.stats?.inliers ?? "—"} accent="#38BDF8" />
            <StatCard testId={IDS.statOutliers} icon={AlertTriangle} label="Outlier" value={data?.stats?.outliers ?? "—"} accent="#F59E0B" />
          </div>

          {/* Upload Zone (when no data) */}
          {!data && (
            <div className="p-4">
              <label
                data-testid={IDS.dropzone}
                htmlFor={IDS.fileInput}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`block cursor-pointer rounded-lg border-2 border-dashed border-white/15 p-6 text-center hover:border-blue-400 hover:bg-blue-500/5 transition-all ${dragOver ? "drop-active" : ""}`}
              >
                {loading ? (
                  <div className="flex flex-col items-center gap-2 text-slate-300">
                    <Loader2 size={28} className="animate-spin text-blue-400" />
                    <div className="text-sm font-semibold">İşleniyor...</div>
                    <div className="text-[11px] text-slate-500 font-mono">Bölgeler hesaplanıyor</div>
                  </div>
                ) : (
                  <>
                    <FileSpreadsheet size={32} className="mx-auto text-blue-400 mb-2" strokeWidth={1.5} />
                    <div className="text-sm font-semibold text-slate-100">Excel dosyanızı buraya bırakın</div>
                    <div className="text-[11px] text-slate-500 mt-1">veya tıklayıp seçin · .xlsx, .xls, .csv</div>
                    <div className="mt-3 text-[10px] text-slate-500 font-mono leading-relaxed">
                      Beklenen kolonlar:<br/>
                      <span className="text-slate-400">SAP Kodu · Bayi Adı · x · y</span>
                    </div>
                  </>
                )}
              </label>
            </div>
          )}

          {/* Outlier control */}
          <div className="px-3 py-3 border-b border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-1.5">
                <Sliders size={11} /> Outlier Eşiği (Bayi Bazlı)
              </div>
              <div className="font-mono text-sm font-bold text-blue-400">{outlierPct.toFixed(1)}%</div>
            </div>
            <input
              data-testid={IDS.outlierSlider}
              type="range"
              min="90"
              max="99.5"
              step="0.5"
              value={outlierPct}
              onChange={(e) => setOutlierPct(parseFloat(e.target.value))}
              onMouseUp={() => data && uploadedName && fileInputRef.current?.files?.[0] && handleFile(fileInputRef.current.files[0])}
              className="w-full accent-blue-500"
              disabled={loading}
            />
            <div className="text-[10px] text-slate-500 mt-1 font-mono">Değeri değiştir → yeniden hesaplanır</div>
          </div>

          {/* Dealer list */}
          {data && (
            <>
              <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    data-testid={IDS.dealerSearch}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Bayi ara..."
                    className="w-full bg-[#111726] border border-white/10 rounded-md pl-7 pr-2 py-1.5 text-[12px] text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                </div>
                <button
                  data-testid={IDS.toggleAllDealers}
                  onClick={toggleAll}
                  className="text-[10px] uppercase font-semibold px-2 py-1.5 rounded bg-white/5 hover:bg-white/10 border border-white/10"
                  title="Tümünü aç/kapa"
                >
                  {visibleSet.size === data.dealers.length ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto custom-scroll py-1">
                {filteredDealers.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-xs">Bayi bulunamadı</div>
                ) : filteredDealers.map(d => (
                  <DealerRow
                    key={d.id}
                    dealer={d}
                    visible={visibleSet.has(d.id)}
                    isolated={isolatedId === d.id}
                    onToggle={onToggle}
                    onIsolate={onIsolate}
                    onFocus={onFocus}
                    onColorChange={onColorChange}
                    onColorReset={onColorReset}
                  />
                ))}
              </div>
            </>
          )}
        </aside>

        {/* Map */}
        <main ref={mapWrapperRef} className="flex-1 relative" data-testid={IDS.mapContainer}>
          <MapContainer
            center={TURKEY_CENTER}
            zoom={6}
            bounds={TURKEY_BOUNDS}
            className="w-full h-full"
            preferCanvas={true}
            style={{ background: "#070A0F" }}
          >
            <TileLayer url={tileConfig.url} attribution={tileConfig.attribution} />
            <MapController target={flyTarget} resetKey={resetKey} onZoomChange={setMapZoom} />

            {viewMode === "voronoi" && filteredGeojson && (
              <GeoJSON
                key={"vor:" + geoJsonKey + ":" + hoveredDealer}
                data={filteredGeojson}
                style={geoJsonStyle}
                onEachFeature={onEachFeature}
              />
            )}

            {viewMode === "admin" && adminGeojson && (
              <GeoJSON
                key={"adm:" + geoJsonKey + ":" + hoveredDealer + ":" + adminGeojson.features.length}
                data={adminGeojson}
                style={(f) => ({
                  fillColor: f.properties.fill,
                  color: f.properties.stroke,
                  weight: hoveredDealer === f.properties.dealerId ? 2 : 0.6,
                  fillOpacity: f.properties.source === "nearest" ? 0.35 : (hoveredDealer === f.properties.dealerId ? 0.75 : 0.6),
                  opacity: 1,
                  lineJoin: "round",
                })}
                onEachFeature={(f, layer) => {
                  const p = f.properties;
                  layer.bindTooltip(
                    `<div style="font-family:'Plus Jakarta Sans'; padding:2px 4px;">
                      <div style="font-weight:700; color:#F8FAFC;">${p.name}</div>
                      <div style="font-size:11px; color:#94A3B8;">${p.dealer}</div>
                      <div style="font-size:10px; color:#64748B; font-family:'JetBrains Mono';">${p.count} müşteri${p.source === "nearest" ? " · en yakın atama" : ""}</div>
                    </div>`,
                    { sticky: true, direction: "top", offset: [0, -8], opacity: 0.95 }
                  );
                  layer.on("mouseover", () => setHoveredDealer(p.dealerId));
                  layer.on("mouseout", () => setHoveredDealer(null));
                }}
              />
            )}

            {/* Customer points (optional) */}
            {showPoints && data && dealersView.filter(d => displayedIds.has(d.id)).flatMap(d =>
              d.points.map((p, i) => (
                <CircleMarker
                  key={`${d.id}-${i}-${d.fill}`}
                  center={[p.lat, p.lng]}
                  radius={2}
                  pathOptions={{ color: d.stroke, fillColor: d.fill, fillOpacity: 1, weight: 0 }}
                />
              ))
            )}

            {/* Outlier points */}
            {filteredOutliers && filteredOutliers.features.map((f, i) => (
              <CircleMarker
                key={`out-${i}`}
                center={[f.geometry.coordinates[1], f.geometry.coordinates[0]]}
                radius={4}
                pathOptions={{ color: "#EF4444", fillColor: "#EF4444", fillOpacity: 0.7, weight: 1, dashArray: "2,2" }}
              >
                <Tooltip>
                  <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11 }}>
                    <b>Outlier</b><br/>SAP: {f.properties.sap}<br/>{f.properties.dealer}
                  </div>
                </Tooltip>
              </CircleMarker>
            ))}

            {/* Dealer name labels (permanent, canvas-safe) */}
            {showLabels && data && dealersView.filter(d => displayedIds.has(d.id)).map(d => {
              const icon = L.divIcon({
                className: "",
                html: `<div class="dealer-label-inner" style="border-color:${d.stroke}">${d.name}</div>`,
                iconSize: null,
                iconAnchor: [0, 0],
              });
              return (
                <Marker
                  key={`lbl-${d.id}-${d.fill}`}
                  position={[d.centroid[1], d.centroid[0]]}
                  icon={icon}
                  interactive={false}
                  keyboard={false}
                />
              );
            })}
          </MapContainer>

          {/* Floating controls */}
          <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
            <div className="bg-[#111726]/90 backdrop-blur-md border border-white/10 rounded-lg overflow-hidden flex" data-testid="view-mode-toggle">
              <button
                data-testid="view-mode-voronoi"
                onClick={() => setViewMode("voronoi")}
                className={`px-3 py-1.5 text-[11px] font-semibold ${viewMode === "voronoi" ? "bg-blue-500 text-white" : "text-slate-300 hover:bg-white/5"}`}
                title="Voronoi tabanlı bayilik bölgeleri"
              >
                <Target size={12} className="inline mr-1" /> Voronoi
              </button>
              <button
                data-testid="view-mode-admin"
                onClick={() => setViewMode("admin")}
                className={`px-3 py-1.5 text-[11px] font-semibold ${viewMode === "admin" ? "bg-blue-500 text-white" : "text-slate-300 hover:bg-white/5"}`}
                title="Türkiye il-ilçe sınırları (zoom ile geçişli)"
              >
                <Grid3x3 size={12} className="inline mr-1" /> İl-İlçe
              </button>
            </div>

            {viewMode === "admin" && (
              <div className="bg-[#111726]/90 backdrop-blur-md border border-white/10 rounded-lg px-3 py-1.5 text-[10px] font-mono text-slate-400 text-center">
                <span className="text-slate-500">Zoom {mapZoom} · </span>
                <span className="text-blue-400 font-bold uppercase">{adminLayerKind === "il" ? "İl" : "İlçe"}</span>
              </div>
            )}

            <div className="bg-[#111726]/90 backdrop-blur-md border border-white/10 rounded-lg overflow-hidden flex">
              <button
                onClick={() => setTileStyle("mahalle")}
                className={`px-3 py-1.5 text-[11px] font-semibold ${tileStyle === "mahalle" ? "bg-blue-500 text-white" : "text-slate-300 hover:bg-white/5"}`}
              >
                <Layers size={12} className="inline mr-1" /> Mahalle
              </button>
              <button
                onClick={() => setTileStyle("standart")}
                className={`px-3 py-1.5 text-[11px] font-semibold ${tileStyle === "standart" ? "bg-blue-500 text-white" : "text-slate-300 hover:bg-white/5"}`}
              >
                Standart
              </button>
              <button
                onClick={() => setTileStyle("dark")}
                className={`px-3 py-1.5 text-[11px] font-semibold ${tileStyle === "dark" ? "bg-blue-500 text-white" : "text-slate-300 hover:bg-white/5"}`}
              >
                Karanlık
              </button>
            </div>

            <button
              data-testid={IDS.fitTurkeyBounds}
              onClick={() => setResetKey(k => k + 1)}
              className="bg-[#111726]/90 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 text-[11px] font-semibold hover:bg-[#182238] flex items-center gap-1.5"
            >
              <RotateCcw size={12} /> Türkiye
            </button>

            <button
              data-testid={IDS.toggleCustomerDots}
              onClick={() => setShowPoints(!showPoints)}
              className={`backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 text-[11px] font-semibold flex items-center gap-1.5 ${showPoints ? "bg-blue-500 text-white" : "bg-[#111726]/90 hover:bg-[#182238]"}`}
            >
              <Users size={12} /> Noktalar
            </button>

            <button
              data-testid="toggle-labels-button"
              onClick={() => setShowLabels(!showLabels)}
              className={`backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 text-[11px] font-semibold flex items-center gap-1.5 ${showLabels ? "bg-blue-500 text-white" : "bg-[#111726]/90 hover:bg-[#182238]"}`}
            >
              <FileText size={12} /> Etiketler
            </button>

            <button
              data-testid={IDS.toggleOutlierDots}
              onClick={() => setShowOutliers(!showOutliers)}
              className={`backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 text-[11px] font-semibold flex items-center gap-1.5 ${showOutliers ? "bg-amber-500 text-white" : "bg-[#111726]/90 hover:bg-[#182238]"}`}
            >
              <AlertTriangle size={12} /> Outliers
            </button>
          </div>

          {/* Empty state overlay */}
          {!data && !loading && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[500]">
              <div className="text-center bg-[#0B0F17]/80 backdrop-blur-md border border-white/10 rounded-xl px-8 py-6 pointer-events-auto">
                <MapIcon size={40} className="mx-auto text-blue-400 mb-3" strokeWidth={1.5} />
                <div className="font-display text-lg font-bold mb-1">Türkiye Bayi Haritası</div>
                <div className="text-[12px] text-slate-400 max-w-xs">
                  Sol panelden Excel dosyanızı yükleyin. Her bayinin bölgesi otomatik olarak <span className="text-blue-400 font-semibold">içbükey (concave hull)</span> yöntemiyle çizilecektir.
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0B0F17]/60 backdrop-blur-sm z-[600]">
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={40} className="animate-spin text-blue-400" />
                <div className="font-mono text-sm text-slate-300">Bölgeler hesaplanıyor...</div>
              </div>
            </div>
          )}

          <DataDrawer
            open={drawerOpen && !!data}
            onClose={() => setDrawerOpen(false)}
            dealers={data?.dealers?.filter(d => displayedIds.has(d.id)) || []}
            outliers={data?.outliers ? { features: data.outliers.features.filter(f => {
              const idMap = Object.fromEntries((data.dealers||[]).map(d => [d.name, d.id]));
              return displayedIds.has(idMap[f.properties.dealer]);
            }) } : null}
          />
        </main>
      </div>
    </div>
  );
}
